import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth, can } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, FileDown, MessageCircle, Receipt } from "lucide-react";
import { WhatsAppDialog, downloadAuthFile } from "@/components/WhatsAppDialog";

const statusBadge = {
  pending: { bg: "bg-[#FFFAEB]", color: "text-[#B54708]", border: "border-[#F79009]", label: "Pending" },
  partial: { bg: "bg-[#EFF4FF]", color: "text-[#002FA7]", border: "border-[#002FA7]", label: "Partial" },
  paid: { bg: "bg-[#ECFDF3]", color: "text-[#027A48]", border: "border-[#039855]", label: "Paid" },
  overdue: { bg: "bg-[#FEF3F2]", color: "text-[#B42318]", border: "border-[#D92D20]", label: "Overdue" },
};

const blankLine = () => ({ description: "", hsn_sac: "", qty: 1, rate: 0, gst_rate: 18, amount: 0 });

const emptyForm = () => ({
  invoice_number: `INV-${Date.now().toString().slice(-6)}`,
  client_id: "",
  issue_date: new Date().toISOString().slice(0, 10),
  due_date: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
  status: "pending",
  notes: "",
  description: "",
  bank_id: "",
  payment_mode: "Credit",
  payment_received: 0,
  lines: [blankLine()],
});

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function Invoices() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [banks, setBanks] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [waOpen, setWaOpen] = useState(false);
  const [waCtx, setWaCtx] = useState(null);

  const load = async () => {
    const [a, b, c] = await Promise.all([api.get("/invoices"), api.get("/clients"), api.get("/banks")]);
    setItems(a.data);
    setClients(b.data);
    setBanks(c.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    const def = banks.find(b => b.is_default);
    setEditing(null);
    setForm({ ...emptyForm(), bank_id: def?.id || "" });
    setOpen(true);
  };

  const openEdit = (inv) => {
    setEditing(inv);
    setForm({
      invoice_number: inv.invoice_number,
      client_id: inv.client_id,
      issue_date: inv.issue_date,
      due_date: inv.due_date,
      status: inv.status,
      notes: inv.notes || "",
      description: inv.description || "",
      bank_id: inv.bank_id || "",
      payment_mode: inv.payment_mode || "Credit",
      payment_received: inv.payment_received || 0,
      lines: (inv.lines && inv.lines.length) ? inv.lines : [{ ...blankLine(), description: inv.description || "", rate: inv.amount || 0, amount: inv.amount || 0 }],
    });
    setOpen(true);
  };

  // Recompute totals client-side for preview
  const subtotal = form.lines.reduce((s, l) => s + (Number(l.qty || 0) * Number(l.rate || 0)), 0);
  const taxTotal = form.lines.reduce((s, l) => s + (Number(l.qty || 0) * Number(l.rate || 0) * Number(l.gst_rate || 0) / 100), 0);
  const cgst = +(taxTotal / 2).toFixed(2);
  const sgst = +(taxTotal / 2).toFixed(2);
  const total = +(subtotal + taxTotal).toFixed(2);
  const balance = +(total - Number(form.payment_received || 0)).toFixed(2);

  const updateLine = (idx, key, val) => {
    const lines = form.lines.map((l, i) => i === idx ? { ...l, [key]: val } : l);
    setForm({ ...form, lines });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        payment_received: parseFloat(form.payment_received || 0),
        bank_id: form.bank_id || null,
        lines: form.lines.map(l => ({
          description: l.description,
          hsn_sac: l.hsn_sac || "",
          qty: parseFloat(l.qty || 1),
          rate: parseFloat(l.rate || 0),
          gst_rate: parseFloat(l.gst_rate || 0),
        })),
      };
      if (editing) await api.put(`/invoices/${editing.id}`, payload);
      else await api.post("/invoices", payload);
      toast.success("Invoice saved");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    await api.delete(`/invoices/${id}`);
    load();
  };

  const downloadPdf = async (inv) => {
    try {
      await downloadAuthFile(`/invoices/${inv.id}/pdf`, `invoice-${inv.invoice_number}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) { toast.error("Could not download PDF"); }
  };

  const shareWhatsApp = (inv) => {
    const client = clients.find(c => c.id === inv.client_id);
    const msg = `Hello ${inv.client_name},\n\nInvoice #${inv.invoice_number}\nTotal: ₹${fmt(inv.total)}\nBalance Due: ₹${fmt(inv.balance)}\nDue Date: ${inv.due_date}\n\nKindly arrange the payment at the earliest.\n\nRegards,\nTAXINPHO Office`;
    setWaCtx({ phone: client?.phone || "", message: msg, label: `invoice-${inv.invoice_number}.pdf` });
    setWaOpen(true);
  };

  const totalBilled = items.reduce((s, i) => s + (i.total || 0), 0);
  const collected = items.reduce((s, i) => s + (i.payment_received || 0), 0);
  const outstanding = items.reduce((s, i) => s + (i.balance || 0), 0);

  const canView = can(user, "invoices_view");
  const canWrite = can(user, "invoices_write");

  return (
    <div className="p-6 md:p-8 max-w-[1600px]">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-caps mb-2">Billing</p>
          <h1 className="font-display font-black text-4xl tracking-tighter">{canView ? "Invoices & Fees" : "Create Invoice"}</h1>
          {!canView && <p className="text-zinc-600 mt-2">You can create new invoices.</p>}
        </div>
        {canWrite && (
          <button onClick={openNew} data-testid="new-invoice" className="bg-zinc-900 text-white px-4 py-2.5 font-semibold flex items-center gap-2 hover:bg-zinc-800">
            <Plus size={16} /> New Invoice
          </button>
        )}
      </div>

      {canView ? (
        <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-zinc-200 p-5"><p className="label-caps mb-2">Total Billed</p><div className="font-display font-black text-3xl tracking-tighter">₹{fmt(totalBilled)}</div></div>
        <div className="bg-white border border-zinc-200 p-5"><p className="label-caps mb-2">Collected</p><div className="font-display font-black text-3xl tracking-tighter text-[#039855]">₹{fmt(collected)}</div></div>
        <div className="bg-white border border-zinc-200 p-5"><p className="label-caps mb-2">Outstanding</p><div className="font-display font-black text-3xl tracking-tighter text-[#D92D20]">₹{fmt(outstanding)}</div></div>
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <th className="text-left px-6 py-3 label-caps">#</th>
              <th className="text-left px-6 py-3 label-caps">Client</th>
              <th className="text-left px-6 py-3 label-caps">Date</th>
              <th className="text-right px-6 py-3 label-caps">Total</th>
              <th className="text-right px-6 py-3 label-caps">Received</th>
              <th className="text-right px-6 py-3 label-caps">Balance</th>
              <th className="text-left px-6 py-3 label-caps">Status</th>
              <th className="text-right px-6 py-3 label-caps"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="8" className="p-10 text-center text-sm text-zinc-500 font-mono">[ no invoices yet ]</td></tr>}
            {items.map(inv => {
              const s = statusBadge[inv.status] || statusBadge.pending;
              return (
                <tr key={inv.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-6 py-3 font-mono text-xs">{inv.invoice_number}</td>
                  <td className="px-6 py-3 font-medium text-sm">{inv.client_name}</td>
                  <td className="px-6 py-3 text-sm">{inv.issue_date}</td>
                  <td className="px-6 py-3 text-sm font-mono font-bold text-right">₹{fmt(inv.total)}</td>
                  <td className="px-6 py-3 text-sm font-mono text-right text-[#039855]">₹{fmt(inv.payment_received)}</td>
                  <td className="px-6 py-3 text-sm font-mono text-right text-[#D92D20]">₹{fmt(inv.balance)}</td>
                  <td className="px-6 py-3"><span className={`inline-block px-2.5 py-0.5 text-xs font-medium border ${s.bg} ${s.color} ${s.border}`}>{s.label}</span></td>
                  <td className="px-6 py-3 text-right whitespace-nowrap">
                    <button onClick={() => downloadPdf(inv)} title="Download PDF" data-testid={`pdf-invoice-${inv.id}`} className="p-1.5 hover:bg-zinc-100 mr-1"><FileDown size={14} /></button>
                    <button onClick={() => shareWhatsApp(inv)} title="WhatsApp" className="p-1.5 hover:bg-green-50 text-[#25D366] mr-1"><MessageCircle size={14} /></button>
                    {can(user, "invoices_write") && <button onClick={() => openEdit(inv)} className="p-1.5 hover:bg-zinc-100 mr-1"><Pencil size={14} /></button>}
                    {can(user, "delete") && <button onClick={() => remove(inv.id)} className="p-1.5 hover:bg-red-50 text-[#D92D20]"><Trash2 size={14} /></button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
        </>
      ) : (
        <div className="bg-white border border-zinc-200 p-12 text-center">
          <Receipt size={36} className="mx-auto mb-4 text-zinc-300" />
          <h3 className="font-display font-bold text-xl mb-2">Ready to bill?</h3>
          <p className="text-zinc-600 text-sm mb-6">Click "New Invoice" above to generate a tax invoice with HSN/SAC, GST split and bank details. You don't have access to view existing invoices or balances.</p>
          {canWrite && (
            <button onClick={openNew} className="bg-[#002FA7] text-white px-5 py-2.5 font-semibold hover:bg-blue-800 inline-flex items-center gap-2">
              <Plus size={16} /> Start a new invoice
            </button>
          )}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white border border-zinc-200 w-full max-w-4xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
              <h2 className="font-display font-bold text-xl tracking-tight">{editing ? "Edit Invoice" : "New Invoice"}</h2>
              <button onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="label-caps block mb-2">Invoice # *</label>
                  <input required value={form.invoice_number} onChange={e => setForm({ ...form, invoice_number: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 font-mono" />
                </div>
                <div>
                  <label className="label-caps block mb-2">Issue Date *</label>
                  <input required type="date" value={form.issue_date} onChange={e => setForm({ ...form, issue_date: e.target.value })} className="w-full border border-zinc-300 px-3 py-2" />
                </div>
                <div>
                  <label className="label-caps block mb-2">Due Date *</label>
                  <input required type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} className="w-full border border-zinc-300 px-3 py-2" />
                </div>
              </div>

              <div>
                <label className="label-caps block mb-2">Bill To (Client) *</label>
                <select required value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })} data-testid="invoice-client" className="w-full border border-zinc-300 px-3 py-2 bg-white">
                  <option value="">Select client</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}{c.gstin ? ` — ${c.gstin}` : ""}</option>)}
                </select>
              </div>

              {/* Line Items */}
              <div className="border border-zinc-200 p-4 bg-zinc-50">
                <div className="flex items-center justify-between mb-3">
                  <p className="label-caps">Line Items</p>
                  <button type="button" onClick={() => setForm({ ...form, lines: [...form.lines, blankLine()] })} className="text-xs font-semibold border border-zinc-300 bg-white px-2 py-1 hover:bg-zinc-100 flex items-center gap-1" data-testid="add-line">
                    <Plus size={12} /> Add line
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-200">
                        <th className="text-left py-2 label-caps text-[10px]">Description</th>
                        <th className="text-left py-2 label-caps text-[10px]">HSN/SAC</th>
                        <th className="text-right py-2 label-caps text-[10px]">Qty</th>
                        <th className="text-right py-2 label-caps text-[10px]">Rate</th>
                        <th className="text-right py-2 label-caps text-[10px]">GST %</th>
                        <th className="text-right py-2 label-caps text-[10px]">Amount</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.lines.map((ln, i) => {
                        const amt = Number(ln.qty || 0) * Number(ln.rate || 0);
                        return (
                          <tr key={i} className="border-b border-zinc-100">
                            <td className="py-1 pr-1"><input value={ln.description} onChange={e => updateLine(i, "description", e.target.value)} className="w-full bg-white border border-zinc-200 px-2 py-1.5" placeholder="Service / item" /></td>
                            <td className="py-1 pr-1"><input value={ln.hsn_sac || ""} onChange={e => updateLine(i, "hsn_sac", e.target.value)} className="w-24 bg-white border border-zinc-200 px-2 py-1.5 font-mono text-xs" /></td>
                            <td className="py-1 pr-1"><input type="number" step="0.01" value={ln.qty} onChange={e => updateLine(i, "qty", e.target.value)} className="w-16 bg-white border border-zinc-200 px-2 py-1.5 text-right font-mono" /></td>
                            <td className="py-1 pr-1"><input type="number" step="0.01" value={ln.rate} onChange={e => updateLine(i, "rate", e.target.value)} className="w-24 bg-white border border-zinc-200 px-2 py-1.5 text-right font-mono" /></td>
                            <td className="py-1 pr-1">
                              <select value={ln.gst_rate} onChange={e => updateLine(i, "gst_rate", e.target.value)} className="w-20 bg-white border border-zinc-200 px-2 py-1.5">
                                {[0, 5, 12, 18, 28].map(r => <option key={r} value={r}>{r}%</option>)}
                              </select>
                            </td>
                            <td className="py-1 text-right font-mono font-semibold">₹{fmt(amt)}</td>
                            <td className="py-1 pl-2">
                              {form.lines.length > 1 && (
                                <button type="button" onClick={() => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) })} className="text-[#D92D20] hover:bg-red-50 p-1"><Trash2 size={12} /></button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="label-caps block mb-2">Pay To (Bank Account)</label>
                    <select value={form.bank_id} onChange={e => setForm({ ...form, bank_id: e.target.value })} data-testid="invoice-bank" className="w-full border border-zinc-300 px-3 py-2 bg-white">
                      <option value="">No bank (cash / hide on PDF)</option>
                      {banks.map(b => <option key={b.id} value={b.id}>{b.bank_name} · {b.account_number}{b.is_default ? " (default)" : ""}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label-caps block mb-2">Payment Received</label>
                      <input type="number" step="0.01" value={form.payment_received} onChange={e => setForm({ ...form, payment_received: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 font-mono" />
                    </div>
                    <div>
                      <label className="label-caps block mb-2">Payment Mode</label>
                      <select value={form.payment_mode} onChange={e => setForm({ ...form, payment_mode: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 bg-white">
                        {["Credit", "Cash", "Bank", "UPI", "Cheque", "Card"].map(m => <option key={m}>{m}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="label-caps block mb-2">Notes</label>
                    <textarea rows="2" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full border border-zinc-300 px-3 py-2" />
                  </div>
                </div>

                <div className="bg-zinc-900 text-white p-5 space-y-2 self-start">
                  <p className="label-caps text-zinc-400">Summary</p>
                  <div className="flex justify-between text-sm"><span className="text-zinc-400">Subtotal</span><span className="font-mono">₹{fmt(subtotal)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-zinc-400">CGST</span><span className="font-mono">₹{fmt(cgst)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-zinc-400">SGST</span><span className="font-mono">₹{fmt(sgst)}</span></div>
                  <div className="border-t border-zinc-700 my-2"></div>
                  <div className="flex justify-between font-bold text-lg"><span>Total</span><span className="font-mono">₹{fmt(total)}</span></div>
                  <div className="flex justify-between text-sm pt-2"><span className="text-zinc-400">Received</span><span className="font-mono text-[#1ABEDC]">₹{fmt(form.payment_received)}</span></div>
                  <div className="flex justify-between text-base font-bold"><span>Balance Due</span><span className="font-mono text-[#FCA5A5]">₹{fmt(balance)}</span></div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border border-zinc-300 font-semibold">Cancel</button>
                <button type="submit" data-testid="save-invoice" className="bg-zinc-900 text-white px-6 py-2 font-semibold">Save Invoice</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <WhatsAppDialog open={waOpen} onClose={() => setWaOpen(false)} defaultPhone={waCtx?.phone} defaultMessage={waCtx?.message} attachLabel={waCtx?.label} />
    </div>
  );
}
